import type ParseType from 'parse';

import { LabelNameProjector } from '@protocols/domain';
import type { ILabelNamesPort, LabelNamesDto } from '@protocols/use-cases';
import type { Label } from '@telespot/domain';
import { LabelTopology, ObjectTopology, ParseBaseRepository, ParseLabelMapper } from '@telespot/infrastructure/parse';

/** Rows fetched per page. Exported so a spec can size a multi-page result set. */
export const LABEL_NAMES_PAGE_SIZE = 100;

/**
 * The only fields a name is built from. `objectId` is listed explicitly because
 * it is the token pager's cursor: Parse Server would append it anyway, through
 * `AlwaysSelectedKeys`, but asking for what we read keeps paging independent of
 * that behaviour.
 */
const NAME_FIELDS: string[] = [ObjectTopology.ID, LabelTopology.UUID, LabelTopology.LANG, LabelTopology.VALUE];

/**
 * Reads the `Label` table for a set of uuids and groups the rows — one per
 * uuid+language pair — into a localized name per uuid.
 *
 * The read half lives here and the grouping half stays in `LabelNameProjector`,
 * the domain service that already owns it: promoting a fallback language into
 * English is a rule about names, not about Parse. The port's contract is the
 * grouped map, so the adapter is what applies the projection.
 */
export class ParseLabelNamesRepository extends ParseBaseRepository implements ILabelNamesPort {
  private readonly labelMapper = new ParseLabelMapper(this.parse);

  constructor(parse: typeof ParseType, options?: Parse.FullOptions) {
    super(parse, options);
  }

  public async resolve(uuids: string[]): Promise<LabelNamesDto> {
    if (uuids.length === 0) return {};

    // Selected down to the name fields: a uuid stored in every language answers
    // five rows and a large pipeline resolves hundreds of uuids.
    const query = new this.parse.Query(LabelTopology.TABLE).containedIn(LabelTopology.UUID, uuids).select(NAME_FIELDS);

    const labels: Label[] = [];
    for await (const rows of this.getTokenPaginationGenerator({ query, limit: LABEL_NAMES_PAGE_SIZE })) {
      labels.push(...rows.map((row) => this.labelMapper.toDomain(row)));
    }

    return LabelNameProjector.groupByUuid(labels);
  }
}
